import React, { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { downloadCsv } from '../../../services/downloadBlob';
import Button from '../../Button';

export interface ExportCsvButtonProps {
  endpoint: string;
  filename: string;
  params?: Record<string, string | number | undefined>;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Domain-specific shim around the canonical <Button> primitive.
 * Wraps services/downloadBlob.downloadCsv with a busy lock and the
 * undefined-param filter. Renders as a secondary outline button so it
 * sits visually below primary actions on the same toolbar.
 */
export default function ExportCsvButton({
  endpoint,
  filename,
  params,
  disabled,
  children,
  className,
}: ExportCsvButtonProps) {
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      const cleaned: Record<string, string | number> = {};
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            cleaned[key] = value as string | number;
          }
        });
      }
      await downloadCsv(endpoint, filename, cleaned);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      loading={busy}
      className={className}
      leftIcon={<ArrowDownTrayIcon aria-hidden="true" />}
    >
      {children ?? 'Export CSV'}
    </Button>
  );
}
